'use client';

import * as React from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Link, useRouterState } from '@tanstack/react-router';

import { Button } from '@/components/button';
import {
  SITE_LOGO_LABEL,
  siteMoreNav,
  sitePrimaryNav,
  type SiteNavItem,
} from '@/config/site';
import { type MoreNavItem } from '@/lib/data';
import { cn } from '@/lib/utils';

import { AppImage } from './app-image';

const SPRING_TRANSITION = { type: 'spring' as const, bounce: 0, duration: 0.4 };

function useActiveNav() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  const isActive = React.useCallback(
    (href: string) => {
      if (href === '/') {
        return pathname === '/';
      }
      return pathname === href || pathname.startsWith(`${href}/`);
    },
    [pathname]
  );

  return { isActive, pathname };
}

function Logo() {
  return (
    <Link
      to="/"
      className="font-display text-brand text-xl font-bold transition-opacity hover:opacity-75 active:opacity-50"
    >
      {SITE_LOGO_LABEL}
    </Link>
  );
}

function NavLink({
  to,
  children,
  className,
  isActive,
  onNavigate,
}: {
  to: string;
  children: React.ReactNode;
  className?: string;
  isActive?: boolean;
  onNavigate?: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      className={cn(
        'text-ink/70 hover:text-ink active:text-ink relative rounded-full px-3 py-1.5 text-sm font-medium transition-colors active:scale-95',
        isActive &&
          'text-ink bg-white/50 shadow-[inset_0_0.5px_0_0_rgba(255,255,255,0.8)]',
        className
      )}
      aria-current={isActive ? 'page' : undefined}
    >
      {children}
      {isActive && (
        <span
          className="bg-brand/70 absolute bottom-1 left-1/2 size-1 -translate-x-1/2 rounded-full"
          aria-hidden="true"
        />
      )}
    </Link>
  );
}

function FeaturedCard({ item }: { item: MoreNavItem & { type: 'featured' } }) {
  return (
    <Link
      to={item.href}
      className="border-border bg-card hover:border-brand/50 active:border-brand/50 group relative flex flex-col gap-3 overflow-hidden rounded-xl border p-3 transition-all active:scale-[0.98]"
    >
      <div
        className={cn(
          'relative aspect-[16/10] overflow-hidden rounded-lg',
          item.color
        )}
      >
        <AppImage
          src={item.image}
          alt={item.name}
          fill
          className="object-cover transition-transform duration-500 group-hover:scale-105 group-active:scale-105"
        />
      </div>
      <div className="space-y-0.5">
        <p className="font-display text-sm font-semibold">{item.name}</p>
        <p className="text-muted-foreground text-xs leading-snug">
          {item.description}
        </p>
      </div>
    </Link>
  );
}

function MoreDropdown({
  isOpen,
  onClose,
  menuId,
}: {
  isOpen: boolean;
  onClose: () => void;
  menuId: string;
}) {
  const featuredItems = siteMoreNav.filter(
    (item): item is MoreNavItem & { type: 'featured' } =>
      item.type === 'featured'
  );
  const listItems = siteMoreNav.filter((item) => item.type === 'list');

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          id={menuId}
          role="menu"
          initial={{ opacity: 0, y: 8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={SPRING_TRANSITION}
          className="absolute right-0 top-full z-50 mt-2 w-[calc(100vw-2rem)] max-w-md origin-top-right sm:w-[480px]"
        >
          <div className="surface-elevated overflow-hidden rounded-2xl">
            <div className="grid grid-cols-2 gap-3 p-4">
              {featuredItems.map((item) => (
                <FeaturedCard key={item.name} item={item} />
              ))}
            </div>

            <div className="border-border border-t" />

            <div className="p-2">
              {listItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  role="menuitem"
                  onClick={onClose}
                  className="hover:bg-muted active:bg-muted/70 flex items-center justify-between rounded-lg px-3 py-2.5 text-sm transition-colors"
                >
                  <span className="font-medium">{item.name}</span>
                  {item.description && (
                    <span className="text-muted-foreground text-xs">
                      {item.description}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function MobileMenu({
  isOpen,
  onClose,
  menuId,
  menuRef,
  destinations,
}: {
  isOpen: boolean;
  onClose: () => void;
  menuId: string;
  menuRef: React.RefObject<HTMLDivElement | null>;
  destinations: SiteNavItem[];
}) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.button
            type="button"
            aria-label="Close menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={SPRING_TRANSITION}
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            ref={menuRef}
            id={menuId}
            role="dialog"
            aria-modal="true"
            aria-label="Site navigation"
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', bounce: 0.15, duration: 0.45 }}
            className="surface-elevated fixed right-0 top-0 z-50 h-full w-[280px] rounded-none border-l"
          >
            <div className="border-border flex items-center justify-between border-b p-4">
              <span className="font-display text-lg font-semibold">Menu</span>
              <button
                type="button"
                onClick={onClose}
                className="hover:bg-muted active:bg-muted/70 rounded-full p-2 transition-colors"
              >
                <X className="size-5" />
                <span className="sr-only">Close menu</span>
              </button>
            </div>

            <nav aria-label="Mobile" className="p-4">
              <ul className="space-y-1">
                {destinations.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      onClick={onClose}
                      className="hover:bg-muted active:bg-muted/70 flex min-h-11 items-center justify-between rounded-lg p-3 text-sm font-medium transition-colors"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="border-border border-t p-4">
              <Button variant="brand" size="default" asChild className="w-full">
                <Link to="/contact" onClick={onClose}>
                  Get in touch
                </Link>
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export function SiteHeader() {
  const [moreOpen, setMoreOpen] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const mobileTriggerRef = React.useRef<HTMLButtonElement>(null);
  const mobileMenuRef = React.useRef<HTMLDivElement>(null);
  const { isActive, pathname } = useActiveNav();
  const overlay = pathname === '/';
  const mobileMenuId = 'mobile-site-navigation';
  const moreMenuId = 'desktop-more-navigation';
  const destinations = React.useMemo(
    () => [
      ...sitePrimaryNav,
      ...siteMoreNav.map((item) => ({ name: item.name, href: item.href })),
    ],
    []
  );

  const closeMobileMenu = React.useCallback(() => {
    setMobileOpen(false);
    mobileTriggerRef.current?.focus();
  }, []);

  React.useEffect(() => {
    setMoreOpen(false);
    setMobileOpen(false);
  }, [pathname]);

  React.useEffect(() => {
    const handleScroll = () => {
      if (moreOpen) {
        setMoreOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [moreOpen]);

  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (mobileOpen) {
          closeMobileMenu();
        }
        setMoreOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [closeMobileMenu, mobileOpen]);

  React.useEffect(() => {
    if (!mobileOpen) {
      return;
    }

    const firstFocusable = mobileMenuRef.current?.querySelector<HTMLElement>(
      'a[href], button:not([disabled])'
    );
    firstFocusable?.focus();
  }, [mobileOpen]);

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 640px)');

    const handleViewportChange = () => {
      if (mediaQuery.matches && mobileOpen) {
        closeMobileMenu();
      }
    };

    mediaQuery.addEventListener('change', handleViewportChange);
    return () => mediaQuery.removeEventListener('change', handleViewportChange);
  }, [closeMobileMenu, mobileOpen]);

  return (
    <div
      className={cn(
        'sticky top-0 z-50 w-full overflow-visible',
        overlay && 'h-0'
      )}
    >
      <div className="mx-auto flex w-full max-w-6xl flex-col items-center">
        <header className="surface-elevated relative top-4 z-50 mx-auto my-5 flex w-full max-w-6xl items-center justify-between gap-4 rounded-full px-4 py-2 sm:top-6 sm:my-8 sm:px-6">
          <div className="flex w-full items-center justify-between gap-4">
            <Logo />

            <nav
              aria-label="Primary"
              className="hidden items-center gap-1 sm:flex"
            >
              {sitePrimaryNav.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  isActive={isActive(item.href)}
                  onNavigate={() => setMoreOpen(false)}
                >
                  {item.name}
                </NavLink>
              ))}

              <div className="relative">
                <button
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={moreOpen}
                  aria-controls={moreMenuId}
                  onClick={() => setMoreOpen((open) => !open)}
                  className={cn(
                    'flex min-h-11 items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium transition-colors active:scale-95',
                    moreOpen
                      ? 'bg-brand text-primary-foreground'
                      : 'text-ink/70 hover:text-ink hover:bg-white/45'
                  )}
                >
                  <span>More</span>
                  <ChevronDown
                    className={cn(
                      'size-4 transition-transform duration-200',
                      moreOpen && 'rotate-180'
                    )}
                    aria-hidden="true"
                  />
                </button>

                <MoreDropdown
                  isOpen={moreOpen}
                  onClose={() => setMoreOpen(false)}
                  menuId={moreMenuId}
                />
              </div>
            </nav>

            <div className="flex items-center gap-2">
              <Button
                variant="brand"
                size="pill"
                asChild
                className="hidden sm:inline-flex"
              >
                <Link to="/contact">Get in touch</Link>
              </Button>

              <Button
                ref={mobileTriggerRef}
                variant="outline"
                size="icon"
                className="border-border sm:hidden"
                aria-expanded={mobileOpen}
                aria-controls={mobileMenuId}
                onClick={() => setMobileOpen(true)}
              >
                <Menu className="size-4" />
                <span className="sr-only">Open menu</span>
              </Button>
            </div>
          </div>

          <MobileMenu
            isOpen={mobileOpen}
            onClose={closeMobileMenu}
            menuId={mobileMenuId}
            menuRef={mobileMenuRef}
            destinations={destinations}
          />
        </header>
      </div>
    </div>
  );
}
